import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { Task, TimerMode } from '@/types/task';
import { AuditSubmitData } from '@/types/audit';
import { saveFocusSession } from './useFocusSessions';
import { supabase } from '@/integrations/supabase/client';
import { fireSuccessConfetti } from '@/lib/confetti';
import { notificationService } from '@/services/notificationService';
import { triggerTimerAlarm } from '@/hooks/useHaptic';
import { syncWidgetWithTimer, getMilestoneConfig } from '@/lib/widgetPlugin';
import { syncDesktopWithTimer } from '@/lib/desktopBridge';
import { liveActivityService } from '@/services/liveActivityService';

type TimerState = 'idle' | 'running' | 'paused' | 'break' | 'complete' | 'post_session_audit';

interface SavedTimerState {
  taskId: string | null;
  taskContent: string | null;
  timeRemaining: number;
  elapsedTime: number;
  sessionDuration: number;
  timerMode: TimerMode | null;
  timerState: TimerState;
  savedAt: number;
  sessionStartedAt: number | null;
}

// Task creation callback type
type AddTaskCallback = (
  content: string, 
  day: string | null, 
  duration?: number, 
  dueTime?: string, 
  isSideQuest?: boolean,
  completed?: boolean
) => Promise<Task | null>;

interface PomodoroTimeContextType {
  timeRemaining: number;
  elapsedTime: number;
}

interface PomodoroContextType {
  activeTask: Task | null;
  timerState: TimerState;
  sessionDuration: number;
  breakDuration: number;
  timerMode: TimerMode | null;
  startFocus: (task: Task, mode: TimerMode, duration?: number | null) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  stopTimer: () => void;
  skipBreak: () => void;
  setSessionDuration: (minutes: number) => void;
  setBreakDuration: (minutes: number) => void;
  hasSavedSession: boolean;
  resumeSavedSession: () => void;
  clearSavedSession: () => void;
  handleSessionComplete: (needsMoreTime: boolean) => void;
  showPostSessionAudit: boolean;
  handlePostSessionAudit: (data: AuditSubmitData, completeTask?: boolean) => Promise<void>;
  completedTask: Task | null;
  finishTask: () => void;
  onToggleComplete: ((taskId: string) => void) | null;
  setOnToggleComplete: (callback: ((taskId: string) => void) | null) => void;
  onAddTask: AddTaskCallback | null;
  setOnAddTask: (callback: AddTaskCallback | null) => void;
  // Legacy compatibility
  workType: 'focus' | 'admin' | null;
}

const PomodoroTimeContext = createContext<PomodoroTimeContextType | undefined>(undefined);
const PomodoroContext = createContext<PomodoroContextType | undefined>(undefined);

const DEFAULT_SESSION_DURATION = 25;
const DEFAULT_BREAK_DURATION = 5;
const STORAGE_KEY = 'pomodoro-timer-state';

function saveTimerState(state: SavedTimerState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function loadTimerState(): SavedTimerState | null {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (!saved) return null;
  try {
    return JSON.parse(saved);
  } catch {
    return null;
  }
}

function clearTimerState() {
  localStorage.removeItem(STORAGE_KEY);
}

// Map timer mode to work type for backwards compatibility
function getWorkTypeFromMode(mode: TimerMode): 'focus' | 'admin' {
  return mode === 'maker' ? 'focus' : 'admin';
}

export function PomodoroProvider({ children }: { children: ReactNode }) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [completedTask, setCompletedTask] = useState<Task | null>(null);
  const [timerState, setTimerState] = useState<TimerState>('idle');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [sessionDuration, setSessionDurationState] = useState(DEFAULT_SESSION_DURATION);
  const [breakDuration, setBreakDurationState] = useState(DEFAULT_BREAK_DURATION);
  const [timerMode, setTimerMode] = useState<TimerMode | null>(null);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [onToggleComplete, setOnToggleCompleteState] = useState<((taskId: string) => void) | null>(null);
  const [onAddTask, setOnAddTaskState] = useState<AddTaskCallback | null>(null);
  const sessionStartedAt = useRef<Date | null>(null);
  const pendingAuditNotes = useRef<string>('');
  // Refs for latest tick values so stopTimer/finishTask can read current value without being in deps (avoids recreating callbacks every second)
  const timeRemainingRef = useRef(0);
  const elapsedTimeRef = useRef(0);
  const liveActivityStartedRef = useRef(false);
  useEffect(() => {
    timeRemainingRef.current = timeRemaining;
    elapsedTimeRef.current = elapsedTime;
  }, [timeRemaining, elapsedTime]);

  // Legacy compatibility
  const workType = timerMode ? getWorkTypeFromMode(timerMode) : null;

  // Wrapper for setOnToggleComplete to match expected signature
  const setOnToggleComplete = useCallback((callback: ((taskId: string) => void) | null) => {
    setOnToggleCompleteState(() => callback);
  }, []);

  // Wrapper for setOnAddTask
  const setOnAddTask = useCallback((callback: AddTaskCallback | null) => {
    setOnAddTaskState(() => callback);
  }, []);

  // Wrapper for setSessionDuration
  const setSessionDuration = useCallback((minutes: number) => {
    setSessionDurationState(minutes);
  }, []);

  // Wrapper for setBreakDuration  
  const setBreakDuration = useCallback((minutes: number) => {
    setBreakDurationState(minutes);
  }, []);

  // Check for saved session on mount
  useEffect(() => {
    const saved = loadTimerState();
    if (saved && (saved.timeRemaining > 0 || saved.elapsedTime > 0) && saved.timerState !== 'idle') {
      setHasSavedSession(true);
    }
  }, []);

  // Save state when timer is paused or stopped with time remaining
  useEffect(() => {
    if (timerState === 'paused' && (timeRemaining > 0 || elapsedTime > 0) && activeTask) {
      saveTimerState({
        taskId: activeTask.id,
        taskContent: activeTask.content,
        timeRemaining,
        elapsedTime,
        sessionDuration,
        timerMode,
        timerState,
        savedAt: Date.now(),
        sessionStartedAt: sessionStartedAt.current?.getTime() || null,
      });
    }
  }, [timerState, timeRemaining, elapsedTime, activeTask, sessionDuration, timerMode]);

  // Helper to save focus session to database
  const saveSessionToDb = useCallback(async (
    task: Task, 
    mode: TimerMode, 
    durationMins: number, 
    startedAt: Date,
    auditNotes?: string
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      await saveFocusSession({
        userId: user.id,
        taskId: task.id,
        taskContent: task.content,
        workType: getWorkTypeFromMode(mode),
        timerMode: mode,
        durationMinutes: durationMins,
        startedAt,
        auditNotes,
      });
    } catch (err) {
      console.error('Failed to save focus session:', err);
    }
  }, []);

  // Track pause time for accurate elapsed calculation
  const pauseOffset = useRef<number>(0);
  const pauseStartedAt = useRef<number | null>(null);

  // Recalculate time from start timestamp - resilient to background suspension
  const recalculateTime = useCallback(() => {
    if (!sessionStartedAt.current) return;
    
    const now = Date.now();
    const startTime = sessionStartedAt.current.getTime();
    const rawElapsed = Math.floor((now - startTime) / 1000);
    const actualElapsed = Math.max(0, rawElapsed - pauseOffset.current);
    
    setElapsedTime(actualElapsed);
    
    if (timerMode !== 'maker' && timerState === 'running') {
      // Manager mode countdown
      const remaining = sessionDuration * 60 - actualElapsed;
      if (remaining <= 0) {
        // Session complete - trigger alarm haptic and notification
        triggerTimerAlarm();
        notificationService.refreshPermission();
        notificationService.sendTimerCompleteNotification(activeTask?.content);
        
        if (activeTask && sessionStartedAt.current) {
          saveSessionToDb(activeTask, timerMode!, sessionDuration, sessionStartedAt.current);
        }
        setCompletedTask(activeTask);
        setTimerState('complete');
        clearTimerState();
        setTimeRemaining(0);
      } else {
        setTimeRemaining(remaining);
      }
    } else if (timerState === 'break') {
      // Break countdown
      const remaining = breakDuration * 60 - actualElapsed;
      if (remaining <= 0) {
        // Break over - send notification
        notificationService.refreshPermission();
        notificationService.sendBreakOverNotification(activeTask?.content);
        
        setTimerState('idle');
        setActiveTask(null);
        setTimerMode(null);
        setTimeRemaining(0);
      } else {
        setTimeRemaining(remaining);
      }
    }
  }, [timerMode, timerState, sessionDuration, breakDuration, activeTask, saveSessionToDb]);

  // Handle visibility changes - recalculate time when user returns to app
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && (timerState === 'running' || timerState === 'break')) {
        recalculateTime();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [timerState, recalculateTime]);

  // Timer effect - run interval only when visible to reduce background work and heat
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const startInterval = () => {
      if (timerState === 'running' || timerState === 'break') {
        if (!document.hidden) {
          interval = setInterval(recalculateTime, 1000);
        }
      }
    };

    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        stopInterval();
        startInterval();
      }
    };

    startInterval();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopInterval();
    };
  }, [timerState, recalculateTime]);

  // Sync timer state to iOS lock screen widget (defer first run so native bridge is ready)
  const [bridgeReady, setBridgeReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setBridgeReady(true), 300);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!bridgeReady) return;
    syncWidgetWithTimer({
      timerState,
      activeTask,
      timerMode,
      timeRemaining,
      elapsedTime,
      sessionDuration,
    });
    // Sync to Electron desktop widgets (no-op on iOS/web)
    syncDesktopWithTimer({
      timerState,
      activeTask,
      timerMode,
      timeRemaining,
      elapsedTime,
      sessionDuration,
    });
  }, [bridgeReady, timerState, activeTask, timerMode, timeRemaining, elapsedTime, sessionDuration]);

  // Sync timer state to iOS Live Activity (lock screen / Dynamic Island)
  useEffect(() => {
    if (!bridgeReady) return;
    // End Live Activity when timer stops (check first, before activeTask may be null)
    if (timerState === 'idle' || timerState === 'complete' || timerState === 'post_session_audit' || timerState === 'break') {
      if (liveActivityStartedRef.current) {
        liveActivityService.endTimer();
        liveActivityStartedRef.current = false;
      }
      return;
    }

    if (!activeTask || !timerMode || (timerState !== 'running' && timerState !== 'paused')) return;

    const mode = timerMode === 'maker' ? 'focus' : 'admin';
    const goalMinutes = timerMode === 'maker' ? sessionDuration : undefined;
    const { count, intervalSeconds } = getMilestoneConfig(mode, sessionDuration, goalMinutes);
    const achievedMilestones = Math.min(Math.floor(elapsedTime / intervalSeconds), count);
    const timeToNextMilestone = Math.round(intervalSeconds - (elapsedTime % intervalSeconds));
    const isPaused = timerState === 'paused';

    if (!liveActivityStartedRef.current) {
      // Start Live Activity when entering running state
      liveActivityService.startTimer({
        taskName: activeTask.content,
        sessionDuration: sessionDuration * 60,
        timeToNextMilestone,
        currentMilestone: achievedMilestones,
        totalMilestones: count,
        motivation: 'Stay focused! 💪',
        timerMode,
        isPaused,
      }).then((success) => {
        if (success) liveActivityStartedRef.current = true;
      });
    } else {
      // Update Live Activity on pause/resume or milestone change
      liveActivityService.updateTimer({
        timeToNextMilestone,
        currentMilestone: achievedMilestones,
        totalMilestones: count,
        isPaused,
      });
    }
  }, [bridgeReady, timerState, activeTask, timerMode, timeRemaining, elapsedTime, sessionDuration]);

  const startFocus = useCallback((task: Task, mode: TimerMode, duration?: number | null) => {
    setActiveTask(task);
    setTimerMode(mode);
    setHasSavedSession(false);
    sessionStartedAt.current = new Date();
    pauseOffset.current = 0; // Reset pause offset for new session
    pauseStartedAt.current = null;
    clearTimerState();
    
    if (mode === 'maker') {
      // Maker mode: Count up from 0, goal-based milestones (duration = goal in minutes)
      const goalMinutes = duration || 60;
      setElapsedTime(0);
      setTimeRemaining(0);
      setSessionDuration(goalMinutes);
    } else {
      // Manager mode: Custom duration countdown
      const focusDuration = duration || DEFAULT_SESSION_DURATION;
      setTimeRemaining(focusDuration * 60);
      setSessionDuration(focusDuration);
      setElapsedTime(0);
    }
    
    setTimerState('running');
  }, []);

  const pauseTimer = useCallback(() => {
    if (timerState === 'running' || timerState === 'break') {
      pauseStartedAt.current = Date.now();
      setTimerState('paused');
    }
  }, [timerState]);

  const resumeTimer = useCallback(() => {
    if (timerState === 'paused') {
      // Add paused duration to offset
      if (pauseStartedAt.current) {
        pauseOffset.current += Math.floor((Date.now() - pauseStartedAt.current) / 1000);
        pauseStartedAt.current = null;
      }
      setTimerState(activeTask ? 'running' : 'break');
    }
  }, [timerState, activeTask]);

  const stopTimer = useCallback(async () => {
    const currentTimeRemaining = timeRemainingRef.current;
    const currentElapsedTime = elapsedTimeRef.current;

    // Calculate actual focused time (read from refs to avoid callback depending on ticking state)
    let actualMinutes: number = 0;

    if (activeTask && timerMode && sessionStartedAt.current) {
      if (timerMode === 'maker') {
        actualMinutes = Math.round(currentElapsedTime / 60);
      } else {
        const elapsedSeconds = sessionDuration * 60 - currentTimeRemaining;
        actualMinutes = Math.round(elapsedSeconds / 60);
      }

      if (actualMinutes >= 1) {
        await saveSessionToDb(activeTask, timerMode, actualMinutes, sessionStartedAt.current);
      }
    }

    if (actualMinutes >= 1 && activeTask) {
      setCompletedTask(activeTask);
      setTimerState('post_session_audit');
      setActiveTask(null);
      clearTimerState();
    } else {
      setTimerState('idle');
      setActiveTask(null);
      setTimerMode(null);
      setTimeRemaining(0);
      setElapsedTime(0);
      clearTimerState();
    }

    sessionStartedAt.current = null;
  }, [activeTask, sessionDuration, timerMode, saveSessionToDb]);

  // Handle Live Activity button taps (pause/cancel from lock screen)
  useEffect(() => {
    const onPause = () => pauseTimer();
    const onCancel = () => stopTimer();
    liveActivityService.on('pause', onPause);
    liveActivityService.on('cancel', onCancel);
    return () => {
      liveActivityService.off('pause', onPause);
      liveActivityService.off('cancel', onCancel);
    };
  }, [pauseTimer, stopTimer]);

  const skipBreak = useCallback(() => {
    if (timerState === 'break') {
      setTimerState('idle');
      setActiveTask(null);
      setTimerMode(null);
      setTimeRemaining(0);
    }
  }, [timerState]);

  const resumeSavedSession = useCallback(() => {
    const saved = loadTimerState();
    if (saved && (saved.timeRemaining > 0 || saved.elapsedTime > 0)) {
      setActiveTask({
        id: saved.taskId!,
        content: saved.taskContent!,
        user_id: '',
        day: '',
        due_time: null,
        duration: null,
        completed: false,
        is_side_quest: false,
        is_main_task: false,
        order_index: 0,
        tag_id: null,
        folder_id: null,
        created_at: '',
        updated_at: '',
      });
      setTimeRemaining(saved.timeRemaining);
      setElapsedTime(saved.elapsedTime || 0);
      setSessionDuration(saved.sessionDuration);
      setTimerMode(saved.timerMode);
      setTimerState('running');
      setHasSavedSession(false);
      // Restore session start time
      sessionStartedAt.current = saved.sessionStartedAt ? new Date(saved.sessionStartedAt) : new Date();
      clearTimerState();
    }
  }, []);

  const clearSavedSession = useCallback(() => {
    clearTimerState();
    setHasSavedSession(false);
  }, []);

  const handleSessionComplete = useCallback((needsMoreTime: boolean) => {
    if (needsMoreTime && completedTask) {
      // Save that this task needs more time - give them another block when they resume
      saveTimerState({
        taskId: completedTask.id,
        taskContent: completedTask.content,
        timeRemaining: sessionDuration * 60, // Full session duration for the extra block
        elapsedTime: 0,
        sessionDuration,
        timerMode,
        timerState: 'paused',
        savedAt: Date.now(),
        sessionStartedAt: null, // Will start fresh when resumed
      });
      setHasSavedSession(true);
      setCompletedTask(null);
      setTimerState('idle');
      // Return to home so they can click Resume and get a few more minutes, then survey after that block
      return;
    }

    // All done - show post-session audit to log accomplishments and side quests
    setTimerState('post_session_audit');
  }, [completedTask, sessionDuration, timerMode]);

  const handlePostSessionAudit = useCallback(async (data: AuditSubmitData, completeTask = false) => {
    const today = new Date().toISOString().split('T')[0];
    let itemsCreated = 0;

    // Create tasks from audit data if we have the addTask callback
    if (onAddTask) {
      for (const item of data.accomplishments) {
        try {
          const task = await onAddTask(item, today, undefined, undefined, false, true);
          if (task) itemsCreated++;
        } catch (err) {
          console.error('Failed to create accomplishment task:', err);
        }
      }
      for (const item of data.sideQuests) {
        try {
          const task = await onAddTask(item, today, undefined, undefined, true, true);
          if (task) itemsCreated++;
        } catch (err) {
          console.error('Failed to create side quest task:', err);
        }
      }
      for (const item of data.todoItems) {
        try {
          const task = await onAddTask(item, today, undefined, undefined, false, false);
          if (task) itemsCreated++;
        } catch (err) {
          console.error('Failed to create todo task:', err);
        }
      }
    }

    // If user chose "Finish task", mark the session task complete
    if (completeTask && completedTask && onToggleComplete) {
      onToggleComplete(completedTask.id);
    }

    fireSuccessConfetti();

    setTimerState('idle');
    setActiveTask(null);
    setCompletedTask(null);
    setTimerMode(null);
    setTimeRemaining(0);
    setElapsedTime(0);
    setHasSavedSession(false);
    sessionStartedAt.current = null;
    clearTimerState();
  }, [onAddTask, onToggleComplete, completedTask]);

  // Computed property for showing post-session audit
  const showPostSessionAudit = timerState === 'post_session_audit';

  const finishTask = useCallback(async () => {
    if (activeTask) {
      if (timerMode && sessionStartedAt.current) {
        let actualMinutes: number;
        if (timerMode === 'maker') {
          actualMinutes = Math.round(elapsedTimeRef.current / 60);
        } else {
          actualMinutes = Math.round((Date.now() - sessionStartedAt.current.getTime()) / 60000);
        }
        await saveSessionToDb(activeTask, timerMode, actualMinutes, sessionStartedAt.current);
      }

      if (onToggleComplete) {
        onToggleComplete(activeTask.id);
      }

      clearTimerState();
      setTimerState('idle');
      setActiveTask(null);
      setTimerMode(null);
      setTimeRemaining(0);
      setElapsedTime(0);
      sessionStartedAt.current = null;

      fireSuccessConfetti();
    }
  }, [activeTask, onToggleComplete, timerMode, saveSessionToDb]);

  const stableContextValue = useMemo(
    () => ({
      activeTask,
      timerState,
      sessionDuration,
      breakDuration,
      timerMode,
      workType,
      startFocus,
      pauseTimer,
      resumeTimer,
      stopTimer,
      skipBreak,
      setSessionDuration,
      setBreakDuration,
      hasSavedSession,
      resumeSavedSession,
      clearSavedSession,
      handleSessionComplete,
      showPostSessionAudit,
      handlePostSessionAudit,
      completedTask,
      finishTask,
      onToggleComplete,
      setOnToggleComplete,
      onAddTask,
      setOnAddTask,
    }),
    [
      activeTask,
      timerState,
      sessionDuration,
      breakDuration,
      timerMode,
      workType,
      hasSavedSession,
      showPostSessionAudit,
      completedTask,
      onToggleComplete,
      onAddTask,
      startFocus,
      pauseTimer,
      resumeTimer,
      stopTimer,
      skipBreak,
      setSessionDuration,
      setBreakDuration,
      resumeSavedSession,
      clearSavedSession,
      handleSessionComplete,
      handlePostSessionAudit,
      finishTask,
      setOnToggleComplete,
      setOnAddTask,
    ]
  );

  return (
    <PomodoroTimeContext.Provider value={{ timeRemaining, elapsedTime }}>
      <PomodoroContext.Provider value={stableContextValue}>
        {children}
      </PomodoroContext.Provider>
    </PomodoroTimeContext.Provider>
  );
}

export function usePomodoro() {
  const context = useContext(PomodoroContext);
  if (context === undefined) {
    throw new Error('usePomodoro must be used within a PomodoroProvider');
  }
  return context;
}

export function usePomodoroTime() {
  const context = useContext(PomodoroTimeContext);
  if (context === undefined) {
    throw new Error('usePomodoroTime must be used within a PomodoroProvider');
  }
  return context;
}

// Legacy export for backwards compatibility
export type WorkType = 'focus' | 'admin';
